import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Users, Plus, Edit2, Trash2, User, 
  CheckCircle, X, UserCheck, UserX, Loader2, BarChart3,
  TrendingUp, CreditCard, Mail, Phone, Search, Bell,
  Gift, AlertTriangle
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { fr, es, enUS } from 'date-fns/locale';
import axios from 'axios';

import { API } from '../config/api';
import CoachStats from '../components/CoachStats';
import { LegalAdminPanel } from '../components/LegalAdminPanel';
import CoachInsightsPanel from '../components/CoachInsightsPanel';
import HealthPanel from '../components/HealthPanel';
import DataExportPanel from '../components/DataExportPanel';
import PlanningAdminPanel from '../components/PlanningAdminPanel';
import StatsProPanel from '../components/StatsProPanel';
import ClientsDrilldownPanel from '../components/ClientsDrilldownPanel';
import MonthlyTrendPanel from '../components/MonthlyTrendPanel';
import CapacitySimPanel from '../components/CapacitySimPanel';
import RelanceFormulePanel from '../components/RelanceFormulePanel';
import PrivilegeReminderPanel from '../components/PrivilegeReminderPanel';
import PrivilegeAuditPanel from '../components/PrivilegeAuditPanel';
import CleanupTestUsersPanel from '../components/CleanupTestUsersPanel';
import DataQualityPanel from '../components/DataQualityPanel';
import CoachesAdminPanel from '../components/CoachesAdminPanel';
import StudioOpsPanel from '../components/StudioOpsPanel';
import StudioConfigPanel from '../components/StudioConfigPanel';
import SubscriptionLaunchPanel from '../components/SubscriptionLaunchPanel';
import ResiliationAdminPanel from '../components/ResiliationAdminPanel';
import EngagementDashboardPanel from '../components/EngagementDashboardPanel';
import RelanceCampaignPanel from '../components/RelanceCampaignPanel';
import EmailLogPanel from '../components/EmailLogPanel';
import ContentPanel from '../components/ContentPanel';
import ScheduleSurveyPanel from '../components/ScheduleSurveyPanel';
import EventAdminPanel from '../components/EventAdminPanel';

export default function CoachDashboard() {
  const { t, language } = useLanguage();
  const { isAuthenticated, isCoach, user } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('today');
  const [drilldown, setDrilldown] = useState(null);
  const [todayClasses, setTodayClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [deletingClientEmail, setDeletingClientEmail] = useState(null);
  const [confirmDeleteClient, setConfirmDeleteClient] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteWord, setDeleteWord] = useState('');
  const [deleteMotif, setDeleteMotif] = useState('');
  const [testUsers, setTestUsers] = useState([]);
  const [showTestUsers, setShowTestUsers] = useState(false);
  const [testDeleteWord, setTestDeleteWord] = useState('');
  const [revenue, setRevenue] = useState(null);
  const [contactRequests, setContactRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  
  const [classForm, setClassForm] = useState({
    name: '', description: '', instructor_name: user?.name || '',
    capacity: 6, start_time: '', end_time: '', room: 'Studio Principal'
  });

  const locales = { fr, es, en: enUS };

  useEffect(() => {
    if (!isAuthenticated || !isCoach) { navigate('/'); return; }
    fetchData();
  }, [isAuthenticated, isCoach]);

  useEffect(() => {
    if (activeTab === 'clients' && clients.length === 0) fetchClients();
    if (activeTab === 'revenue' && !revenue) fetchRevenue();
    if (activeTab === 'contacts' && contactRequests.length === 0) fetchContacts();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [classesRes, statsRes] = await Promise.all([
        axios.get(`${API}/coach/today`, { withCredentials: true }),
        axios.get(`${API}/coach/stats`, { withCredentials: true })
      ]);
      setTodayClasses(classesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/coach/clients`, { withCredentials: true });
      setClients(res.data);
    } catch (error) { console.error(error); }
  };

  const openDeleteDialog = async (client) => {
    setConfirmDeleteClient(client);
    setDeletePreview(null); setDeleteWord(''); setDeleteMotif('');
    try {
      const res = await axios.get(`${API}/admin/users/${encodeURIComponent(client.email)}/deletion-preview`, { withCredentials: true });
      setDeletePreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de charger le récapitulatif.');
      setConfirmDeleteClient(null);
    }
  };

  const fetchTestUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/test-users`, { withCredentials: true });
      setTestUsers(res.data.users || []);
    } catch { /* silencieux */ }
  };

  const handleDeleteTestUsers = async () => {
    if (testDeleteWord !== 'SUPPRIMER') { toast.error('Saisissez le mot SUPPRIMER pour confirmer.'); return; }
    setDeletingClientEmail('bulk');
    try {
      const res = await axios.post(`${API}/admin/test-users/delete`, { confirm: 'SUPPRIMER' }, { withCredentials: true });
      toast.success(`${res.data.deleted_users} compte(s) de test supprimé(s).`);
      setShowTestUsers(false); setTestDeleteWord('');
      fetchTestUsers(); fetchClients();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible.');
    } finally { setDeletingClientEmail(null); }
  };

  const handleDeleteClient = async (email) => {
    if (deleteWord !== 'SUPPRIMER') { toast.error('Saisissez le mot SUPPRIMER pour confirmer.'); return; }
    setDeletingClientEmail(email);
    try {
      const res = await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/delete`,
        { confirm: 'SUPPRIMER', motif: deleteMotif || null }, { withCredentials: true });
      toast.success(`${res.data.name || email} supprimée définitivement (paiements anonymisés, journal admin créé).`);
      setClients((prev) => prev.filter((c) => (c.email || '').toLowerCase() !== email.toLowerCase()));
      setConfirmDeleteClient(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Suppression impossible.";
      toast.error(msg);
    } finally {
      setDeletingClientEmail(null);
    }
  };

  const fetchRevenue = async () => {
    try {
      const res = await axios.get(`${API}/coach/revenue`, { withCredentials: true });
      setRevenue(res.data);
    } catch (error) { console.error(error); }
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/contact/requests`, { withCredentials: true });
      setContactRequests(res.data);
    } catch (error) { console.error(error); }
  };

  const handleCreateClass = async () => {
    setActionLoading('create');
    try {
      await axios.post(`${API}/classes`, classForm, { withCredentials: true });
      toast.success('Cours créé avec succès');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(null); }
  };

  const handleUpdateClass = async () => {
    if (!editingClass) return;
    setActionLoading('update');
    try {
      await axios.put(`${API}/classes/${editingClass._id}`, classForm, { withCredentials: true });
      toast.success('Cours modifié');
      setEditingClass(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(null); }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Supprimer ce cours ?')) return;
    setActionLoading(classId);
    try {
      await axios.delete(`${API}/classes/${classId}`, { withCredentials: true });
      toast.success('Cours supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(null); }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    setActionLoading(bookingId);
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, { status }, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(null); }
  };

  const handleGenerateReminders = async () => {
    setActionLoading('reminders');
    try {
      const res = await axios.post(`${API}/notifications/generate-reminders`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Erreur lors de la génération des rappels');
    } finally { setActionLoading(null); }
  };

  const resetForm = () => {
    setClassForm({ name: '', description: '', instructor_name: user?.name || '', capacity: 6, start_time: '', end_time: '', room: 'Studio Principal' });
  };

  const openEditDialog = (cls) => {
    setEditingClass(cls);
    setClassForm({
      name: cls.name, description: cls.description || '', instructor_name: cls.instructor_name,
      capacity: cls.capacity, start_time: format(new Date(cls.start_time), "yyyy-MM-dd'T'HH:mm"),
      end_time: format(new Date(cls.end_time), "yyyy-MM-dd'T'HH:mm"), room: cls.room || 'Studio Principal'
    });
  };

  const formatTime = (dateStr) => format(new Date(dateStr), 'HH:mm', { locale: locales[language] || locales.fr });

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.email.toLowerCase().includes(clientSearch.toLowerCase())
  );

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="font-heading font-light text-3xl sm:text-4xl text-[#493329] tracking-tight mb-1" data-testid="coach-dashboard-title">
            {t('dashboard')}
          </h1>
          <p className="text-[#7A6455]" data-testid="admin-greeting">Bonjour Administrateur</p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <Button
            variant="outline"
            onClick={handleGenerateReminders}
            disabled={actionLoading === 'reminders'}
            className="border-[#E9DDCC] text-[#C99A55] rounded-full"
            data-testid="generate-reminders-btn"
          >
            <Bell className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Rappels J-1
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-full" data-testid="create-class-btn">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {t('createClass')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#E9DDCC] rounded-2xl max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#493329]">{t('createClass')}</DialogTitle>
              </DialogHeader>
              <ClassForm form={classForm} setForm={setClassForm} onSubmit={handleCreateClass} loading={actionLoading === 'create'} t={t} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Calendar} label="Cours aujourd'hui" value={stats.today_classes} color="#C99A55" testId="stat-today-classes" />
          <StatCard icon={Users} label="Réservations" value={stats.today_bookings} color="#C99A55" testId="stat-today-bookings" />
          <StatCard icon={User} label="Total clients" value={stats.total_clients} color="#C76542" testId="stat-total-clients" />
          <StatCard icon={CreditCard} label="Abonnements actifs" value={revenue?.active_subscriptions || '—'} color="#C99A55" testId="stat-active-subs" />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-[#E9DDCC] rounded-2xl p-1.5 mb-6 h-auto flex flex-wrap">
          <TabsTrigger value="today" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm">
            Aujourd'hui
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm" data-testid="tab-clients">
            Clients
          </TabsTrigger>
          <TabsTrigger value="revenue" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm" data-testid="tab-revenue">
            Revenus
          </TabsTrigger>
          <TabsTrigger value="contacts" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm" data-testid="tab-contacts">
            Contacts
          </TabsTrigger>
          <TabsTrigger value="planning" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm" data-testid="tab-planning">
            Planning
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm" data-testid="tab-stats">
            Statistiques
          </TabsTrigger>
          <TabsTrigger value="coachs" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm" data-testid="tab-coachs">
            Coachs
          </TabsTrigger>
          <TabsTrigger value="studio" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm" data-testid="tab-studio">
            Studio
          </TabsTrigger>
          <TabsTrigger value="sante" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm" data-testid="tab-sante">
            Santé
          </TabsTrigger>
          <TabsTrigger value="legal" className="flex-1 min-w-[80px] rounded-xl py-2.5 data-[state=active]:bg-[#C99A55] data-[state=active]:text-white text-[#7A6455] text-sm" data-testid="tab-legal">
            Légal
          </TabsTrigger>
        </TabsList>

        {/* ===== TODAY TAB ===== */}
        <TabsContent value="today">
          {todayClasses.length === 0 ? (
            <Card className="bg-white border-[#E9DDCC] rounded-2xl">
              <CardContent className="py-16 text-center">
                <Calendar className="w-12 h-12 text-[#E9DDCC] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#7A6455]">{t('noClasses')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {todayClasses.map((cls, index) => (
                <Card key={cls._id} className="bg-white border-[#E9DDCC] rounded-2xl animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }} data-testid={`class-${cls._id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-heading font-medium text-lg text-[#493329]">{cls.name}</h3>
                        <Badge className="bg-[#F0E8D9] text-[#C99A55] rounded-full">{formatTime(cls.start_time)} - {formatTime(cls.end_time)}</Badge>
                        <Badge className="capacity-available rounded-full">{cls.booked_count}/{cls.capacity}</Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(cls)} className="text-[#7A6455] hover:text-[#C99A55]" data-testid={`edit-class-${cls._id}`}>
                          <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteClass(cls._id)} disabled={actionLoading === cls._id} className="text-[#C76542] hover:bg-[#C76542]/10" data-testid={`delete-class-${cls._id}`}>
                          {actionLoading === cls._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={1.5} />}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#7A6455] mb-3">
                      {cls.instructor_name} · {cls.room}
                      {cls.waitlist_count > 0 && <span className="text-[#C76542] ml-2">· {cls.waitlist_count} en attente</span>}
                    </p>
                    {cls.participants.length === 0 ? (
                      <p className="text-sm text-[#7A6455] italic">Aucun participant</p>
                    ) : (
                      <div className="space-y-2">
                        {cls.participants.map((p) => (
                          <div key={p.booking_id} className="flex items-center justify-between p-3 bg-[#F0E8D9] rounded-xl" data-testid={`participant-${p.booking_id}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                                <User className="w-4 h-4 text-[#C99A55]" strokeWidth={1.5} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#493329]">{p.name}</p>
                                <p className="text-xs text-[#7A6455]">{p.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <StatusBadge status={p.status} t={t} />
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => handleUpdateStatus(p.booking_id, 'present')} disabled={actionLoading === p.booking_id}
                                  className={`${p.status === 'present' ? 'bg-[#8C9B86]/20 text-[#8C9B86]' : 'text-[#7A6455]'} hover:bg-[#8C9B86]/20`} data-testid={`mark-present-${p.booking_id}`}>
                                  <UserCheck className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleUpdateStatus(p.booking_id, 'absent')} disabled={actionLoading === p.booking_id}
                                  className={`${p.status === 'absent' ? 'bg-[#C76542]/20 text-[#C76542]' : 'text-[#7A6455]'} hover:bg-[#C76542]/20`} data-testid={`mark-absent-${p.booking_id}`}>
                                  <UserX className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ===== CLIENTS TAB ===== */}
        <TabsContent value="clients">
          <div className="mb-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A6455]" strokeWidth={1.5} />
              <Input
                placeholder="Rechercher un client..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="pl-10 bg-white border-[#E9DDCC] rounded-xl"
                data-testid="client-search-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <button onClick={() => { fetchTestUsers(); setShowTestUsers(true); setTestDeleteWord(''); }}
                className="text-xs text-[#AE9885] hover:text-[#C76542] underline" data-testid="test-accounts-btn">
                Gérer les comptes de test…
              </button>
            </div>
          </div>

          <Dialog open={showTestUsers} onOpenChange={setShowTestUsers}>
            <DialogContent className="bg-white border-[#E9DDCC] rounded-2xl max-w-md" data-testid="test-accounts-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#493329]">Comptes de test détectés</DialogTitle>
                <DialogDescription className="text-[#7A6455] text-sm">
                  Suppression groupée avec purge conforme (paiements anonymisés, journal admin).
                </DialogDescription>
              </DialogHeader>
              {testUsers.length === 0 ? (
                <p className="text-sm text-[#7A6455]">Aucun compte de test détecté.</p>
              ) : (
                <>
                  <div className="max-h-48 overflow-y-auto space-y-1.5" data-testid="test-accounts-list">
                    {testUsers.map(u => (
                      <div key={u.email} className="text-xs text-[#493329] p-2 rounded-lg bg-[#F5EFE5]">
                        {u.name || '—'} · <span className="text-[#7A6455]">{u.email}</span>
                      </div>
                    ))}
                  </div>
                  <Input value={testDeleteWord} onChange={(e) => setTestDeleteWord(e.target.value)}
                    placeholder='Tapez SUPPRIMER pour confirmer'
                    className="rounded-xl border-[#C76542]/50" data-testid="test-delete-word-input" />
                  <Button onClick={handleDeleteTestUsers}
                    disabled={deletingClientEmail === 'bulk' || testDeleteWord !== 'SUPPRIMER'}
                    className="w-full bg-[#C76542] hover:bg-[#493329] text-white rounded-xl h-11" data-testid="test-delete-confirm">
                    {deletingClientEmail === 'bulk' ? <Loader2 className="w-4 h-4 animate-spin" /> : `Supprimer ${testUsers.length} compte(s) de test`}
                  </Button>
                </>
              )}
            </DialogContent>
          </Dialog>
          
          {filteredClients.length === 0 ? (
            <Card className="bg-white border-[#E9DDCC] rounded-2xl">
              <CardContent className="py-12 text-center">
                <Users className="w-10 h-10 text-[#E9DDCC] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#7A6455]">Aucun client trouvé</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredClients.map((client) => (
                <Card key={client.id} className="bg-white border-[#E9DDCC] rounded-2xl" data-testid={`client-row-${client.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-[#F0E8D9] flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-[#C99A55]" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-[#493329] truncate">{client.name}</p>
                          <p className="text-xs text-[#7A6455] truncate">{client.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {client.subscription ? (
                          <Badge className="bg-[#8C9B86]/15 text-[#6B7A65] rounded-full text-xs">
                            {client.subscription.plan_name}
                          </Badge>
                        ) : (
                          <Badge className="bg-[#C76542]/15 text-[#A67463] rounded-full text-xs">
                            Sans abonnement
                          </Badge>
                        )}
                        <div className="text-right hidden sm:block">
                          <p className="text-sm text-[#493329]">{client.bookings_this_month} ce mois</p>
                          <p className="text-xs text-[#7A6455]">{client.bookings_total} total</p>
                        </div>
                        {client.phone && (
                          <a href={`tel:${client.phone}`} className="text-[#C99A55] hover:text-[#B08A4F]">
                            <Phone className="w-4 h-4" strokeWidth={1.5} />
                          </a>
                        )}
                        <button
                          onClick={() => openDeleteDialog(client)}
                          className="text-[#C76542] hover:text-[#C76542] transition-colors p-1"
                          title="Supprimer cette cliente"
                          data-testid={`client-delete-btn-${client.id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <p className="text-sm text-[#7A6455] mt-4 text-center">{filteredClients.length} client{filteredClients.length > 1 ? 's' : ''}</p>

          {/* Delete client confirmation modal */}
          <Dialog open={!!confirmDeleteClient} onOpenChange={(open) => !open && setConfirmDeleteClient(null)}>
            <DialogContent className="bg-white border-[#E9DDCC] rounded-2xl max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#493329]">Supprimer cette cliente ?</DialogTitle>
                <DialogDescription className="text-[#7A6455] text-sm">
                  Cette action supprime définitivement le compte, ses réservations et son abonnement.
                </DialogDescription>
              </DialogHeader>
              {confirmDeleteClient && (
                <div className="space-y-4">
                  {!deletePreview ? (
                    <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#C99A55]" /></div>
                  ) : (
                    <>
                      <div className="bg-[#FFF8E6] border border-[#C99A55]/40 rounded-xl p-4 space-y-1" data-testid="delete-preview">
                        <p className="font-medium text-[#493329]">{deletePreview.name}</p>
                        <p className="text-xs text-[#7A6455]">{deletePreview.email}{deletePreview.commune ? ` · ${deletePreview.commune}` : ''}</p>
                        <p className="text-xs text-[#7A6455]">
                          Formule : <strong>{deletePreview.chosen_plan || 'aucune'}</strong> · Fondatrice : <strong>{deletePreview.is_privilege ? 'OUI' : 'non'}</strong>
                        </p>
                        <p className="text-xs text-[#7A6455]">
                          Réservations : <strong>{deletePreview.reservations}</strong> · Mandat SEPA : <strong>{deletePreview.sepa_mandate ? 'OUI' : 'non'}</strong> · Paiements à anonymiser : <strong>{deletePreview.payments_to_anonymize}</strong>
                        </p>
                        {deletePreview.is_test_account && <p className="text-[11px] text-[#8C9B86]">Compte identifié comme compte de test</p>}
                      </div>
                      <p className="text-xs text-[#7A6455] leading-relaxed">
                        Réservations futures annulées · abonnement/mandat Stripe annulés s'ils existent · données de facturation <strong>anonymisées</strong> (conservation comptable 10 ans) · tout le reste effacé définitivement · action journalisée.
                        <strong className="text-[#C76542] block mt-1">Action irréversible.</strong>
                      </p>
                      <Input value={deleteMotif} onChange={(e) => setDeleteMotif(e.target.value)}
                        placeholder="Motif (facultatif, conservé au journal)"
                        className="rounded-xl border-[#E9DDCC]" data-testid="delete-motif-input" />
                      <Input value={deleteWord} onChange={(e) => setDeleteWord(e.target.value)}
                        placeholder='Tapez SUPPRIMER pour confirmer'
                        className="rounded-xl border-[#C76542]/50" data-testid="delete-word-input" />
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setConfirmDeleteClient(null)}
                          variant="outline"
                          className="flex-1 rounded-xl h-11 border-[#E9DDCC] text-[#7A6455]"
                          disabled={!!deletingClientEmail}
                        >
                          Annuler
                        </Button>
                        <Button
                          onClick={() => handleDeleteClient(confirmDeleteClient.email)}
                          className="flex-1 bg-[#C76542] hover:bg-[#493329] text-white rounded-xl h-11"
                          disabled={!!deletingClientEmail || deleteWord !== 'SUPPRIMER'}
                          data-testid="client-delete-confirm"
                        >
                          {deletingClientEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer définitivement'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ===== REVENUE TAB ===== */}
        <TabsContent value="revenue">
          {!revenue ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard icon={CreditCard} label="Revenu ce mois" value={`${revenue.revenue_this_month.toFixed(0)}€`} color="#C99A55" testId="revenue-this-month" />
                <StatCard icon={TrendingUp} label="MRR (récurrent)" value={`${revenue.mrr.toFixed(0)}€`} color="#C99A55" testId="revenue-mrr" />
                <StatCard icon={Users} label="Abonnés actifs" value={revenue.active_subscriptions} color="#C76542" testId="revenue-subs" />
                <StatCard icon={BarChart3} label="Taux remplissage" value={`${revenue.fill_rate}%`} color="#C99A55" testId="revenue-fill-rate" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Plan distribution */}
                <Card className="bg-white border-[#E9DDCC] rounded-2xl">
                  <CardContent className="p-5">
                    <h3 className="font-heading font-medium text-[#493329] mb-4">Répartition des abonnements</h3>
                    {Object.keys(revenue.plan_distribution).length === 0 ? (
                      <p className="text-sm text-[#7A6455]">Aucun abonnement actif</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(revenue.plan_distribution).map(([plan, count]) => (
                          <div key={plan} className="flex items-center justify-between">
                            <span className="text-sm text-[#493329]">{plan}</span>
                            <Badge className="bg-[#F0E8D9] text-[#C99A55] rounded-full">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Key metrics */}
                <Card className="bg-white border-[#E9DDCC] rounded-2xl">
                  <CardContent className="p-5">
                    <h3 className="font-heading font-medium text-[#493329] mb-4">Ce mois-ci</h3>
                    <div className="space-y-3">
                      <MetricRow label="Nouveaux clients" value={revenue.new_clients_this_month} />
                      <MetricRow label="Transactions" value={revenue.transactions_this_month} />
                      <MetricRow label="Capacité totale" value={`${revenue.total_booked_this_month}/${revenue.total_capacity_this_month}`} />
                      <MetricRow label="Mois précédent" value={`${revenue.revenue_prev_month.toFixed(0)}€`} />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Expiring subscriptions */}
              {revenue.expiring_soon.length > 0 && (
                <Card className="bg-[#C76542]/10 border-[#C76542]/30 rounded-2xl">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-[#C76542]" strokeWidth={1.5} />
                      <h3 className="font-heading font-medium text-[#493329]">Abonnements expirant bientôt</h3>
                    </div>
                    <div className="space-y-2">
                      {revenue.expiring_soon.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-white rounded-xl">
                          <div>
                            <p className="text-sm font-medium text-[#493329]">{item.user_name}</p>
                            <p className="text-xs text-[#7A6455]">{item.user_email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-[#C76542]">{item.plan_name}</p>
                            <p className="text-xs text-[#7A6455]">Expire le {format(new Date(item.end_date), 'd MMM', { locale: fr })}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* ===== CONTACTS TAB ===== */}
        <TabsContent value="contacts">
          {contactRequests.length === 0 ? (
            <Card className="bg-white border-[#E9DDCC] rounded-2xl">
              <CardContent className="py-12 text-center">
                <Mail className="w-10 h-10 text-[#E9DDCC] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#7A6455]">Aucune demande de contact</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {contactRequests.map((req) => (
                <Card key={req.id} className="bg-white border-[#E9DDCC] rounded-2xl" data-testid={`contact-request-${req.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-[#493329]">{req.name}</p>
                        <p className="text-xs text-[#7A6455]">{req.email}{req.phone ? ` · ${req.phone}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-[#F0E8D9] text-[#C99A55] rounded-full text-xs">{req.subject}</Badge>
                        <span className="text-xs text-[#7A6455]">{format(new Date(req.created_at), 'd MMM HH:mm', { locale: fr })}</span>
                      </div>
                    </div>
                    <p className="text-sm text-[#7A6455] bg-[#F0E8D9] rounded-xl p-3">{req.message}</p>
                    <div className="flex gap-2 mt-3">
                      <a href={`mailto:${req.email}`}>
                        <Button size="sm" variant="outline" className="border-[#E9DDCC] text-[#C99A55] rounded-full text-xs">
                          <Mail className="w-3 h-3 mr-1" /> Répondre
                        </Button>
                      </a>
                      {req.phone && (
                        <a href={`tel:${req.phone}`}>
                          <Button size="sm" variant="outline" className="border-[#E9DDCC] text-[#C99A55] rounded-full text-xs">
                            <Phone className="w-3 h-3 mr-1" /> Appeler
                          </Button>
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ===== STATS TAB ===== */}
        <TabsContent value="coachs">
          <CoachesAdminPanel />
        </TabsContent>

        <TabsContent value="studio">
          {activeTab === 'studio' && (
            <div className="space-y-6">
              <StudioConfigPanel />
              <ContentPanel />
              <EngagementDashboardPanel />
              <SubscriptionLaunchPanel />
              <RelanceCampaignPanel />
              <ResiliationAdminPanel />
              <StudioOpsPanel />
              <DataExportPanel />
            </div>
          )}
        </TabsContent>

        <TabsContent value="planning">
          {activeTab === 'planning' && (
            <div className="space-y-6">
              <PlanningAdminPanel />
              <ScheduleSurveyPanel />
              <EventAdminPanel />
            </div>
          )}
        </TabsContent>

        <TabsContent value="sante">
          {activeTab === 'sante' && <HealthPanel isAdmin={true} />}
        </TabsContent>

        <TabsContent value="stats">
          {activeTab === 'stats' && (
            drilldown ? (
              <ClientsDrilldownPanel segment={drilldown} onBack={() => setDrilldown(null)} />
            ) : (
              <div className="space-y-6">
                <StatsProPanel onDrilldown={setDrilldown} />
                <DataQualityPanel />
                <CapacitySimPanel />
                <MonthlyTrendPanel />
                <CoachInsightsPanel />
                <RelanceFormulePanel />
                <PrivilegeAuditPanel />
                <PrivilegeReminderPanel />
                <EmailLogPanel />
                <CleanupTestUsersPanel />
                <CoachStats />
              </div>
            )
          )}
        </TabsContent>

        {/* ===== LEGAL TAB ===== */}
        <TabsContent value="legal">
          <LegalAdminPanel />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editingClass} onOpenChange={(open) => !open && setEditingClass(null)}>
        <DialogContent className="bg-white border-[#E9DDCC] rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#493329]">{t('editClass')}</DialogTitle>
          </DialogHeader>
          <ClassForm form={classForm} setForm={setClassForm} onSubmit={handleUpdateClass} loading={actionLoading === 'update'} t={t} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===== SUB-COMPONENTS ===== */

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <Card className="bg-white border-[#E9DDCC] rounded-2xl" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-[#7A6455]">{label}</p>
            <p className="text-2xl font-heading font-medium text-[#493329]">{value}</p>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MetricRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-[#7A6455]">{label}</span>
      <span className="text-sm font-medium text-[#493329]">{value}</span>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const styles = {
    reserved: 'status-reserved', confirmed: 'status-confirmed',
    present: 'status-present', absent: 'status-absent'
  };
  return <Badge className={`${styles[status] || styles.reserved} rounded-full`}>{t(status)}</Badge>;
}

function ClassForm({ form, setForm, onSubmit, loading, t }) {
  return (
    <div className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label className="text-[#493329]">{t('className')}</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" placeholder="Pilates Reformer" data-testid="class-name-input" />
      </div>
      <div className="space-y-2">
        <Label className="text-[#493329]">{t('description')}</Label>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field min-h-[80px]" placeholder="Description..." data-testid="class-description-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#493329]">{t('instructorName')}</Label>
          <Input value={form.instructor_name} onChange={(e) => setForm({ ...form, instructor_name: e.target.value })} className="input-field" data-testid="instructor-name-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#493329]">{t('capacity')}</Label>
          <Input type="number" min="1" max="20" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) })} className="input-field" data-testid="capacity-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#493329]">{t('startTime')}</Label>
          <Input type="datetime-local" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="input-field" data-testid="start-time-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#493329]">{t('endTime')}</Label>
          <Input type="datetime-local" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="input-field" data-testid="end-time-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-[#493329]">{t('room')}</Label>
        <Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="input-field" placeholder="Studio Principal" data-testid="room-input" />
      </div>
      <Button onClick={onSubmit} disabled={loading || !form.name || !form.start_time || !form.end_time} className="w-full bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-xl h-12" data-testid="submit-class-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('save')}
      </Button>
    </div>
  );
}
